import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listenToCustomerBookings, cancelBooking, confirmPayment } from '../services/bookingService';
import StatusTracker from '../components/StatusTracker';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUser, FaTimesCircle, FaRupeeSign } from 'react-icons/fa';
import './MyBookingsPage.css';

const MyBookingsPage = () => {
    const { currentUser } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [cancellingId, setCancellingId] = useState(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        const unsubscribe = listenToCustomerBookings(currentUser.uid, (data) => {
            setBookings(data);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const handleCancel = async (bookingId) => {
        if (!window.confirm('Are you sure you want to cancel this booking?')) return;

        setCancellingId(bookingId);
        try {
            await cancelBooking(bookingId);
        } catch (err) {
            alert('Failed to cancel booking: ' + err.message);
        } finally {
            setCancellingId(null);
        }
    };

    // Handle StatusTracker action (customer can confirm payment)
    const handleStatusAction = async (bookingId, nextStatus) => {
        setActionLoadingId(bookingId);
        try {
            if (nextStatus === 'payment-done') {
                await confirmPayment(bookingId);
            }
        } catch (err) {
            alert('Failed to update status: ' + err.message);
        } finally {
            setActionLoadingId(null);
        }
    };

    const filteredBookings = filter === 'all'
        ? bookings
        : bookings.filter(b => b.status === filter);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'status-pending';
            case 'accepted': return 'status-accepted';
            case 'in-progress': return 'status-inprogress';
            case 'completed': return 'status-completed';
            case 'cancelled': return 'status-cancelled';
            case 'rejected': return 'status-rejected';
            default: return '';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-IN', {
                year: 'numeric', month: 'short', day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    if (!currentUser) {
        return (
            <div className="my-bookings-page">
                <div className="container">
                    <div className="login-prompt">
                        <span className="prompt-icon">🔒</span>
                        <h2>Login Required</h2>
                        <p>Please login to view your bookings.</p>
                        <Link to="/login" className="btn btn-primary">Login Now</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-bookings-page">
            <div className="container">
                <div className="page-header">
                    <h1>My Bookings</h1>
                    <p>Track and manage your service requests</p>
                </div>

                <div className="filter-section">
                    {['all', 'pending', 'accepted', 'in-progress', 'work-paused', 'work-completed', 'payment-done', 'cancelled'].map(f => (
                        <button
                            key={f}
                            className={`filter-btn ${filter === f ? 'active' : ''}`}
                            onClick={() => setFilter(f)}
                        >
                            {f === 'all' ? `All (${bookings.length})` :
                                `${f.charAt(0).toUpperCase() + f.slice(1)} (${bookings.filter(b => b.status === f).length})`
                            }
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading your bookings...</p>
                    </div>
                ) : filteredBookings.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">📋</span>
                        <h3>{filter === 'all' ? 'No bookings yet' : `No ${filter} bookings`}</h3>
                        <p>{filter === 'all'
                            ? 'Browse our services and book a professional to get started!'
                            : `You don't have any ${filter} bookings at the moment.`
                        }</p>
                        {filter === 'all' && (
                            <Link to="/service/electrician" className="btn btn-primary">Browse Services</Link>
                        )}
                    </div>
                ) : (
                    <div className="bookings-list">
                        {filteredBookings.map((booking) => (
                            <div key={booking.id} className={`booking-card ${getStatusColor(booking.status)}`}>
                                <div className="booking-card-header">
                                    <div className="booking-service-info">
                                        <h3>{booking.serviceName}</h3>
                                        <span className={`booking-badge ${booking.status}`}>
                                            {booking.status}
                                        </span>
                                    </div>
                                    <span className="booking-date">
                                        Booked: {formatDate(booking.createdAt)}
                                    </span>
                                </div>

                                {booking.description && (
                                    <p className="booking-description">{booking.description}</p>
                                )}

                                <div className="booking-details">
                                    <div className="detail-item">
                                        <FaMapMarkerAlt />
                                        <span>{booking.address || 'No address provided'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <FaCalendarAlt />
                                        <span>{booking.preferredDate || 'Flexible'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <FaClock />
                                        <span>{booking.preferredTime || 'Flexible'}</span>
                                    </div>
                                    {booking.providerName && (
                                        <div className="detail-item provider-info">
                                            <FaUser />
                                            <span>Provider: <strong>{booking.providerName}</strong></span>
                                        </div>
                                    )}
                                </div>

                                {/* Pricing Display */}
                                {booking.visitFee ? (
                                    <div className="booking-price">
                                        <span className="price-visit">Visit Fee: ₹{booking.visitFee} <small>(incl. 1st hour)</small></span>
                                        <span className="price-rate"> + ₹{booking.hourlyRate || 0}/hr after</span>
                                        {booking.calculatedTotal && (
                                            <span className="final-price"> | Total: ₹{booking.calculatedTotal}</span>
                                        )}
                                    </div>
                                ) : booking.estimatedPrice ? (
                                    <div className="booking-price">
                                        Estimated: ₹{booking.estimatedPrice}
                                        {booking.finalPrice && (
                                            <span className="final-price"> → Final: ₹{booking.finalPrice}</span>
                                        )}
                                    </div>
                                ) : null}

                                {/* Final Bill Breakdown — shown when work is completed */}
                                {(booking.status === 'work-completed' || booking.status === 'payment-done') && booking.calculatedTotal && (
                                    <div className="customer-bill-section">
                                        <h4 className="customer-bill-title"><FaRupeeSign /> Bill Summary</h4>
                                        <div className="customer-bill-rows">
                                            <div className="customer-bill-row">
                                                <span>Visit Fee (incl. 1st hour)</span>
                                                <span>₹{booking.visitFee || 0}</span>
                                            </div>
                                            {(booking.totalHoursWorked || 0) > 1 && (
                                                <div className="customer-bill-row">
                                                    <span>Additional hours ({Math.max(0, (booking.totalHoursWorked - 1 - (booking.overtimeHours || 0))).toFixed(1)} hrs × ₹{booking.hourlyRate || 0})</span>
                                                    <span>₹{Math.round(Math.max(0, booking.totalHoursWorked - 1 - (booking.overtimeHours || 0)) * (booking.hourlyRate || 0))}</span>
                                                </div>
                                            )}
                                            {(booking.overtimeHours || 0) > 0 && (
                                                <div className="customer-bill-row customer-bill-overtime">
                                                    <span>Overtime ({booking.overtimeHours.toFixed(1)} hrs × ₹{Math.round((booking.hourlyRate || 0) * 1.5)})</span>
                                                    <span>₹{Math.round(booking.overtimeHours * (booking.hourlyRate || 0) * 1.5)}</span>
                                                </div>
                                            )}
                                            <div className="customer-bill-row customer-bill-total">
                                                <span>Total ({(booking.totalHoursWorked || 0).toFixed(1)} hrs worked)</span>
                                                <span>₹{booking.calculatedTotal}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Arrival OTP display for customer — shown when provider is on the way */}
                                {booking.arrivalOtp && booking.status === 'accepted' && (
                                    <div className="completion-otp-display">
                                        <div className="otp-display-header">
                                            <span className="otp-display-icon">🔑</span>
                                            <span>Worker Arrival Code</span>
                                        </div>
                                        <div className="otp-code">{booking.arrivalOtp}</div>
                                        <p className="otp-display-hint">
                                            Share this code with your worker when they arrive at your door
                                        </p>
                                    </div>
                                )}

                                {/* Status progress tracker */}
                                <StatusTracker
                                    currentStatus={booking.status}
                                    statusHistory={booking.statusHistory || []}
                                    role="customer"
                                    onAction={(nextStatus) => handleStatusAction(booking.id, nextStatus)}
                                    actionLoading={actionLoadingId === booking.id}
                                />

                                {(booking.status === 'pending' || booking.status === 'accepted') && (
                                    <div className="booking-actions">
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleCancel(booking.id)}
                                            disabled={cancellingId === booking.id}
                                        >
                                            <FaTimesCircle />
                                            {cancellingId === booking.id ? ' Cancelling...' : ' Cancel Booking'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyBookingsPage;
